import MapEmbed from '@/components/feature/MapEmbed';
import LgpdNotice from '@/components/feature/LgpdNotice';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventType: '',
    participants: '',
    dateStart: '',
    dateEnd: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [dateError, setDateError] = useState('');

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: contactRef, isVisible: contactVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: mapRef, isVisible: mapVisible } = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Contato Palestra Silenciosa São Paulo | Orçamento | STIB';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Entre em contato com a STIB Palestra Silenciosa São Paulo. Solicite orçamento para eventos corporativos, conferências e workshops. WhatsApp: (11) 91777-6227. E-mail: contato@stibpalestrasilenciosasp.com.br');
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    const contactSchema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contato - STIB Palestra Silenciosa São Paulo',
      description: 'Página de contato para solicitar orçamentos de palestra silenciosa em São Paulo',
      url: `${siteUrl}/contato`,
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'STIB - Palestra Silenciosa São Paulo',
        telephone: '+55-11-91777-6227',
        email: 'contato@stibpalestrasilenciosasp.com.br',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'São Paulo',
          addressRegion: 'SP',
          addressCountry: 'BR'
        }
      }
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Contato', item: `${siteUrl}/contato` }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'contact-schema';
    script.text = JSON.stringify(contactSchema);
    document.head.appendChild(script);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'contact-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      const existingScript = document.getElementById('contact-schema');
      if (existingScript) existingScript.remove();
      const existingBreadcrumb = document.getElementById('contact-breadcrumb-schema');
      if (existingBreadcrumb) existingBreadcrumb.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);

    if (name === 'dateStart' || name === 'dateEnd') {
      const start = name === 'dateStart' ? value : newFormData.dateStart;
      const end = name === 'dateEnd' ? value : newFormData.dateEnd;
      if (start && end && end < start) {
        setDateError('A data de término não pode ser anterior à data de início.');
      } else {
        setDateError('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.dateStart && formData.dateEnd && formData.dateEnd < formData.dateStart) {
      setDateError('A data de término não pode ser anterior à data de início.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setDateError('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setDateError('');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          eventType: '',
          participants: '',
          dateStart: '',
          dateEnd: '',
          message: ''
        });
      } else {
        setSubmitMessage('Erro ao enviar mensagem. Por favor, tente novamente.');
      }
    } catch (error) {
      setSubmitMessage('Erro ao enviar mensagem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 8000);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[400px] flex items-center justify-center overflow-hidden mt-20" aria-label="Contato Palestra Silenciosa São Paulo">
          <div className="absolute inset-0">
            <img
              src="/img/stib/escritorio-moderno.webp"
              alt="Contato STIB Palestra Silenciosa São Paulo"
              className="w-full h-full object-cover object-top animate-hero-zoom"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/70 via-secondary/50 to-secondary/70 dark:from-black/80 dark:via-black/60 dark:to-black/80"></div>
          </div>

          <div className="absolute top-6 left-0 right-0 z-20">
            <div className="max-w-7xl mx-auto px-6">
              <Breadcrumb variant="light" items={[{ label: 'Home', to: '/' }, { label: 'Contato' }]} />
            </div>
          </div>

          <div ref={heroRef} className="relative z-10 text-center px-6">
            <h1 className={`font-heading font-bold text-6xl md:text-7xl text-white mb-6 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              CONTATO
            </h1>
            <p className={`font-body text-xl text-white/90 max-w-2xl mx-auto transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Entre em contato e transforme seu próximo <strong>evento corporativo</strong>
            </p>
          </div>
        </section>

        {/* Conteúdo */}
        <section className="py-28 bg-gray-50 dark:bg-gray-800" aria-label="Formulário de contato e informações">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={contactRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Informações */}
              <article className={`transition-all duration-700 ${contactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-8">
                  Vamos Conversar
                </h2>
                <p className="font-body text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-12">
                  Preencha o formulário ao lado ou entre em contato diretamente pelos nossos canais. Nossa equipe está pronta para ajudar você a criar um <strong className="dark:text-white">evento memorável</strong>.
                </p>

                <address className="space-y-8 not-italic">
                  {[
                    { icon: 'ri-phone-line', title: 'Telefone', content: '(11) 91777-6227', href: 'tel:+5511917776227' },
                    { icon: 'ri-mail-line', title: 'E-mail', content: 'contato@stibpalestrasilenciosasp.com.br', href: 'mailto:contato@stibpalestrasilenciosasp.com.br' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-4 transition-all duration-500 ${contactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: `${200 + i * 120}ms` }}>
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 flex-shrink-0 hover:bg-primary transition-all duration-300 group">
                        <i className={`${item.icon} text-primary group-hover:text-white text-2xl transition-all duration-300`} aria-hidden="true"></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-2">{item.title}</h3>
                        <p className="font-body text-base text-gray-600 dark:text-gray-300">
                          <a href={item.href} className="hover:text-primary transition-colors">{item.content}</a>
                        </p>
                      </div>
                    </div>
                  ))}
                  <div className={`flex items-start gap-4 transition-all duration-500 ${contactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: '440ms' }}>
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-2">Localização</h3>
                      <p className="font-body text-base text-gray-600 dark:text-gray-300">São Paulo, SP<br />Brasil</p>
                    </div>
                  </div>
                  <div className={`flex items-start gap-4 transition-all duration-500 ${contactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: '560ms' }}>
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 flex-shrink-0">
                      <i className="ri-time-line text-primary text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-2">Horário de Atendimento</h3>
                      <p className="font-body text-base text-gray-600 dark:text-gray-300">Segunda a Sexta: 9h às 18h<br />Sábado: 9h às 13h</p>
                    </div>
                  </div>
                </address>
              </article>

              {/* Formulário */}
              <div className={`bg-white dark:bg-gray-900 rounded-3xl p-10 border border-transparent dark:border-gray-800 transition-all duration-700 delay-200 ${contactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                <h3 className="font-heading font-bold text-3xl text-secondary dark:text-white mb-8">
                  Solicite um Orçamento
                </h3>
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" aria-label="Formulário de solicitação de orçamento">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="contact-name" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Nome Completo *</label>
                      <input type="text" name="name" id="contact-name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">E-mail *</label>
                      <input type="email" name="email" id="contact-email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Telefone *</label>
                      <input type="tel" name="phone" id="contact-phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="contact-company" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Empresa</label>
                      <input type="text" name="company" id="contact-company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="contact-eventType" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tipo de Evento *</label>
                      <select name="eventType" id="contact-eventType" value={formData.eventType} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors">
                        <option value="">Selecione...</option>
                        <option value="Evento Corporativo">Evento Corporativo</option>
                        <option value="Conferência Multilíngue">Conferência Multilíngue</option>
                        <option value="Workshop">Workshop</option>
                        <option value="Silent Disco">Silent Disco</option>
                        <option value="Treinamento">Treinamento</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="contact-participants" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Número de Participantes</label>
                      <input type="text" name="participants" id="contact-participants" value={formData.participants} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="contact-dateStart" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Data do Evento</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="contact-dateStart" className="block font-body text-xs text-gray-500 dark:text-gray-400 mb-1">Início</label>
                          <input type="date" name="dateStart" id="contact-dateStart" value={formData.dateStart} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                        </div>
                        <div>
                          <label htmlFor="contact-dateEnd" className="block font-body text-xs text-gray-500 dark:text-gray-400 mb-1">Término</label>
                          <input type="date" name="dateEnd" id="contact-dateEnd" value={formData.dateEnd} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body transition-colors" />
                        </div>
                      </div>
                      {dateError && (
                        <p className="text-red-600 dark:text-red-400 text-xs font-body mt-2" role="alert">{dateError}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Mensagem *</label>
                      <textarea name="message" id="contact-message" value={formData.message} onChange={handleChange} required maxLength={500} rows={4} className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:border-primary dark:focus:border-primary focus:outline-none font-body resize-none transition-colors" placeholder="Conte-nos mais sobre seu evento..."></textarea>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formData.message.length}/500 caracteres</p>
                    </div>
                    <LgpdNotice kind="contato" className="mb-4 col-span-full" />
            <button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white px-8 py-4 rounded-full font-body font-bold text-base hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                      {isSubmitting ? 'ENVIANDO...' : 'ENVIAR MENSAGEM'}
                    </button>
                    {submitMessage && (
                      <p className={`text-center font-body text-sm ${submitMessage.includes('sucesso') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} role="status">{submitMessage}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Mapa */}
        <section className="py-24 bg-white dark:bg-gray-900" aria-label="Área de atendimento em São Paulo">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={mapRef} className={`text-center mb-12 transition-all duration-700 ${mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <span className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 block">Área de Atendimento</span>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                Atendemos em São Paulo e Região
              </h2>
              <p className="font-body text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                Nossa equipe atua em toda a Grande São Paulo e interior do estado, com logística própria para garantir pontualidade e qualidade no seu evento.
              </p>
            </div>

            <div className={`grid grid-cols-1 lg:grid-cols-3 gap-0 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 transition-all duration-700 delay-200 ${mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <div className="bg-secondary dark:bg-gray-800 p-10 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading font-bold text-2xl text-white mb-8">Onde Atuamos</h3>
                  <ul className="space-y-5">
                    {[
                      { icon: 'ri-map-pin-fill', label: 'São Paulo — Capital', detail: 'Atendimento completo' },
                      { icon: 'ri-map-pin-fill', label: 'Grande São Paulo', detail: 'ABC, Guarulhos, Osasco...' },
                      { icon: 'ri-map-pin-fill', label: 'Interior Paulista', detail: 'Sob consulta' },
                      { icon: 'ri-map-pin-fill', label: 'Outros estados', detail: 'Projetos especiais' },
                    ].map((item, i) => (
                      <li key={i} className={`flex items-start gap-4 transition-all duration-500 ${mapVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: `${400 + i * 100}ms` }}>
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-primary text-lg`}></i>
                        </div>
                        <div>
                          <p className="font-body font-semibold text-white text-sm">{item.label}</p>
                          <p className="font-body text-white/60 text-xs mt-0.5">{item.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="font-body text-white/60 text-xs mb-3 uppercase tracking-wider">Contato rápido</p>
                  <a href="https://wa.me/5511917776227" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-400 text-white px-6 py-3 rounded-full font-body font-semibold text-sm transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-white text-base"></i>
                    </div>
                    Chamar no WhatsApp
                  </a>
                </div>
              </div>
              <div className="lg:col-span-2 h-[480px] w-full">
                <MapEmbed src="https://maps.google.com/maps?q=S%C3%A3o+Paulo%2C+SP%2C+Brasil&t=&z=11&ie=UTF8&iwloc=&output=embed" title="Área de atendimento STIB Palestra Silenciosa - São Paulo" fallbackQuery="São Paulo, SP, Brasil" />
              </div>
            </div>

            <div className={`mt-8 flex flex-wrap gap-3 justify-center transition-all duration-700 delay-500 ${mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              {['São Paulo Centro','Pinheiros','Itaim Bibi','Vila Olímpia','Faria Lima','Paulista','Berrini','Santo André','São Bernardo','Guarulhos','Osasco','Campinas'].map((city) => (
                <span key={city} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs font-semibold px-4 py-2 rounded-full font-body hover:border-primary hover:text-primary transition-colors duration-200 cursor-default">
                  {city}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}